import { useEffect, useState } from "react";
import { api, apiError, fmtINR } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { useNavigate } from "react-router-dom";
import { Star, CalendarBlank, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { openRazorpayCheckout } from "../../lib/razorpay";

const dayLabel = (isoStr) => new Date(isoStr + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function CustomerNewRequest() {
  const [catalog, setCatalog] = useState([]);
  const [category, setCategory] = useState("");
  const [productType, setProductType] = useState("");
  const [service, setService] = useState(null);
  const [form, setForm] = useState({ address: "", pincode: "", city: "", state: "", notes: "" });
  const [pinLoading, setPinLoading] = useState(false);
  const [slotCfg, setSlotCfg] = useState({ slots: [], dates: [], min_date: "", max_date: "", availability: {} });
  const [dateIdx, setDateIdx] = useState(0);
  const [slot, setSlot] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const selectedDate = (slotCfg.dates || [])[dateIdx] || slotCfg.min_date || "";

  useEffect(() => { api.get("/public/services").then((r) => setCatalog(r.data)); }, []);
  useEffect(() => {
    const p = {};
    if (form.city) p.city = form.city;
    if (selectedDate) p.date = selectedDate;
    api.get("/public/booking-slots", { params: p }).then((r) => setSlotCfg(r.data)).catch(() => {});
  }, [form.city, selectedDate]);
  useEffect(() => { setSlot(""); }, [dateIdx]);

  const cat = catalog.find((c) => c.category === category);
  const pt = cat?.product_types.find((p) => p.product_type === productType);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupPin = async (pin) => {
    set("pincode", pin);
    if (!/^\d{6}$/.test(pin)) return;
    setPinLoading(true);
    try { const { data } = await api.get(`/public/pincode/${pin}`); setForm((f) => ({ ...f, pincode: pin, city: data.city, state: data.state })); }
    catch (e) {} finally { setPinLoading(false); }
  };

  const submit = async () => {
    if (!category || !productType || !service) { toast.error("Please pick a service"); return; }
    if (!form.address || !/^\d{6}$/.test(form.pincode)) { toast.error("Address and a valid 6-digit pincode are required"); return; }
    if (!slot) { toast.error("Please select a time slot"); return; }
    if (!agree) { toast.error("Please accept the Terms & Conditions to continue"); return; }
    setBusy(true);
    try {
      const items = [{
        product_category: category, product_type: productType,
        service_type: service.service_type, part_name: service.part_name || "", amount: service.amount,
      }];
      const { data: order } = await api.post("/payments/customer/create-order", {
        items, address: form.address, pincode: form.pincode,
        scheduled_date: selectedDate, slot, notes: form.notes, accept_terms: true,
      });
      if (order.mode === "after") {
        toast.success("Booking confirmed! You'll pay after the service is completed.");
        navigate("/account/requests");
        return;
      }
      const pay = await openRazorpayCheckout(order, { description: "Fixigo service booking" });
      await api.post("/payments/customer/verify", {
        order_ref: order.order_ref, razorpay_order_id: pay.razorpay_order_id,
        razorpay_payment_id: pay.razorpay_payment_id, razorpay_signature: pay.razorpay_signature,
      });
      toast.success("Payment successful! Your booking is confirmed.");
      navigate("/account/requests");
    } catch (e) {
      if (e?.message === "Payment cancelled") toast.error("Payment cancelled");
      else toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Book a Service" subtitle="Pick a service, share your address & slot — we'll auto-assign a partner" />
      <div className="p-8 max-w-2xl">
        <Card className="rounded-sm border-border p-6 space-y-5" data-testid="new-request-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="label-caps">Category</Label>
              <Select value={category} onValueChange={(v) => { setCategory(v); setProductType(""); setService(null); }}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="req-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{catalog.map((c) => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Product Type</Label>
              <Select value={productType} onValueChange={(v) => { setProductType(v); setService(null); }} disabled={!cat}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="req-product-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{cat?.product_types.map((p) => <SelectItem key={p.product_type} value={p.product_type}>{p.product_type}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {pt && (
            <div>
              <Label className="label-caps mb-2 block">Choose Service</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="service-options">
                {pt.services.map((s, i) => {
                  const sel = service && service.service_type === s.service_type && service.part_name === s.part_name;
                  return (
                    <button key={i} type="button" data-testid={`service-opt-${i}`} onClick={() => setService(s)}
                      className={`flex items-center justify-between p-3 rounded-sm border text-left transition-colors duration-200 ${
                        sel ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"}`}>
                      <span className="text-sm font-medium flex items-center gap-1.5">
                        {s.featured && <Star size={13} weight="fill" className="text-amber-500" />}
                        {s.service_type}{s.part_name ? ` · ${s.part_name}` : ""}
                      </span>
                      <span className="font-mono font-semibold text-sm">{fmtINR(s.amount)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div><Label className="label-caps">Service Address *</Label>
            <Textarea data-testid="req-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="rounded-sm mt-1.5" placeholder="Flat / house no., building, street, locality" /></div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-caps">Pincode *</Label>
              <Input data-testid="req-pincode" maxLength={6} value={form.pincode} onChange={(e) => lookupPin(e.target.value.replace(/\D/g, ""))} className="rounded-sm mt-1.5" />
              {pinLoading && <span className="text-[11px] text-primary">Looking up…</span>}
            </div>
            <div><Label className="label-caps">City</Label><Input data-testid="req-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-sm mt-1.5" /></div>
            <div><Label className="label-caps">State</Label><Input data-testid="req-state" value={form.state} onChange={(e) => set("state", e.target.value)} className="rounded-sm mt-1.5" /></div>
          </div>

          <div>
            <Label className="label-caps flex items-center gap-1.5"><CalendarBlank size={14} /> Pick a date</Label>
            <div className="flex gap-2 overflow-x-auto mt-2 pb-1" data-testid="req-date-picker">
              {(slotCfg.dates || []).map((d, i) => (
                <button key={d} type="button" onClick={() => setDateIdx(i)} data-testid={`req-date-${i}`}
                  className={`shrink-0 px-3.5 py-2 rounded-sm text-sm font-semibold border transition-colors duration-200 ${dateIdx === i ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
                  {i === 0 && d === todayIso() ? "Today" : dayLabel(d)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="label-caps flex items-center gap-1.5"><Clock size={14} /> Pick a time slot</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="req-slot-grid">
              {(slotCfg.slots || []).map((s) => {
                const av = (slotCfg.availability || {})[s];
                const full = av?.full;
                return (
                  <button key={s} type="button" disabled={full} onClick={() => setSlot(s)} data-testid={`req-slot-${s.replace(/[^0-9]/g, "").slice(0, 4)}`}
                    className={`px-2 py-2 rounded-sm text-xs font-semibold border transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${slot === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
                    {s}{full ? " · Full" : (av?.remaining != null ? ` · ${av.remaining} left` : "")}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">{slotCfg.min_date ? `Earliest available date: ${dayLabel(slotCfg.min_date)}. Time windows are set by Fixigo.` : "Loading available slots…"}</p>
          </div>

          <div><Label className="label-caps">Notes (optional)</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm mt-1.5" placeholder="Anything the professional should know" /></div>

          {service && (
            <div className="bg-secondary rounded-sm p-4 space-y-2" data-testid="req-amount-card">
              <div className="flex items-center justify-between">
                <span className="label-caps">Total Amount</span>
                <span className="font-heading font-bold text-xl" data-testid="req-amount">{fmtINR(service.amount)}</span>
              </div>
              {service.payment_mode === "after" ? (
                <div className="flex items-center justify-between text-sm text-amber-700"><span>Pay after service</span><span className="font-semibold">₹0 now</span></div>
              ) : service.payment_mode === "partial" ? (
                <>
                  <div className="flex items-center justify-between text-sm text-primary"><span>Booking amount (pay now)</span><span className="font-semibold" data-testid="req-pay-now">{fmtINR(Math.min(service.booking_amount || 0, service.amount))}</span></div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground"><span>Balance after service</span><span>{fmtINR(service.amount - Math.min(service.booking_amount || 0, service.amount))}</span></div>
                </>
              ) : (
                <div className="flex items-center justify-between text-sm text-primary"><span>Pay online now</span><span className="font-semibold" data-testid="req-pay-now">{fmtINR(service.amount)}</span></div>
              )}
            </div>
          )}
          <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="req-terms-label">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border accent-primary" data-testid="req-terms-checkbox" />
            <span className="text-xs text-muted-foreground leading-relaxed">
              I agree to the{" "}
              <a href="/customer-terms" target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline" data-testid="req-terms-link">Customer Terms &amp; Conditions</a>, including the cancellation policy.
            </span>
          </label>
          <Button className="rounded-sm w-full" onClick={submit} disabled={!service || busy || !agree} data-testid="submit-request">{busy ? "Processing…" : (service && service.payment_mode === "after" ? "Confirm Booking" : "Proceed to Pay")}</Button>
        </Card>
      </div>
    </>
  );
}
